import * as signalR from '@microsoft/signalr';
import { SOCKET_URL, USE_MOCK_API } from '../constants';

let connection: signalR.HubConnection | null = null;

export const initSocket = async (): Promise<signalR.HubConnection | null> => {
  if (USE_MOCK_API || !SOCKET_URL) {
    return null; // No connection in mock mode or when SOCKET_URL is not configured
  }

  if (!connection) {
    const token = localStorage.getItem('token');
    connection = new signalR.HubConnectionBuilder()
      .withUrl(`${SOCKET_URL}/hubs/agent-stream`, {
        accessTokenFactory: () => token || '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    try {
      await connection.start();
      console.log('SignalR connected:', connection.connectionId);
    } catch (error) {
      console.error('SignalR connection failed:', error);
      return null;
    }

    connection.onclose(() => {
      console.log('SignalR disconnected');
    });

    connection.onreconnecting(() => {
      console.log('SignalR reconnecting...');
    });

    connection.onreconnected(() => {
      console.log('SignalR reconnected');
    });
  }

  return connection;
};

export const getSocket = (): signalR.HubConnection | null => {
  return connection;
};

export const disconnectSocket = async (): Promise<void> => {
  if (connection) {
    await connection.stop();
    connection = null;
  }
};

export const emitUsage = async (data: { action: string; model: string; chat_id?: string }): Promise<void> => {
  if (connection?.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke('SendUsage', data);
    } catch (error) {
      console.error('Failed to send usage data:', error);
    }
  }
};

// Streaming chat with SignalR
export const startChatStreaming = async (
  conversationId: string,
  onToken: (token: string) => void,
  onComplete: () => void,
  onError: (error: string) => void
): Promise<void> => {
  const conn = getSocket();
  if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
    onError('SignalR connection not available');
    return;
  }

  // Set up event handlers
  const tokenHandler = (convId: string, token: string) => {
    if (convId === conversationId) {
      onToken(token);
    }
  };

  const completeHandler = (convId: string) => {
    if (convId === conversationId) {
      // Clean up handlers
      conn.off('ReceiveToken', tokenHandler);
      conn.off('StreamComplete', completeHandler);
      conn.off('StreamError', errorHandler);
      onComplete();
    }
  };

  const errorHandler = (convId: string, error: string) => {
    if (convId === conversationId) {
      // Clean up handlers
      conn.off('ReceiveToken', tokenHandler);
      conn.off('StreamComplete', completeHandler);
      conn.off('StreamError', errorHandler);
      onError(error);
    }
  };

  // Register handlers
  conn.on('ReceiveToken', tokenHandler);
  conn.on('StreamComplete', completeHandler);
  conn.on('StreamError', errorHandler);
};

// Mock streaming response for development
export const simulateStreamingResponse = async (
  chatId: string,
  messageId: string,
  response: string,
  onChunk: (chunk: string) => void,
  onComplete: () => void,
  onError?: (error: string) => void
): Promise<void> => {
  if (!USE_MOCK_API) return;

  const chunks = response.split(' ');
  for (let i = 0; i < chunks.length; i++) {
    await new Promise((resolve) => setTimeout(resolve, 50 + Math.random() * 100));
    onChunk(chunks[i] + (i < chunks.length - 1 ? ' ' : ''));
  }
  onComplete();
};

export default {
  initSocket,
  getSocket,
  disconnectSocket,
  emitUsage,
  startChatStreaming,
  simulateStreamingResponse,
};